import React, { createContext, useContext, useEffect, useState } from 'react';
import { useOffline } from '../Offline';

const FormContext = createContext();

export const useFormContext = () => {
    return useContext(FormContext);
};

const debounceTime = 500;
let debounce = null;

export const Provider = ({ children, onChange, data, disabled, closeModalOnSubmit, goBackOnSubmit, afterSubmit, afterError, restoreDataOnConditionalChange, conditionalDependencies, layout, resetOnSubmit, availableOffline = false }) => {
    const [formData, setFormData] = useState(data ?? {});
    const [formDataDebounced, setFormDataDebounced] = useState(data ?? {});
    const [formHiddenData, setFormHiddenData] = useState({});
    const [formValidity, setValidity] = useState({});
    const [isValid, setIsValid] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isValidating, setIsValidating] = useState(false);
    const {isOnline} = useOffline();

    const updateField = (name, value) => {
        if (typeof value === 'function') {
            setFormData(prev =>  ({...prev, [name]: value(prev[name]) }));
        } else {
            setFormData(prev =>  ({...prev, [name]: value }));
        }
    };

    const updateValidity = (name, value) => {
        setValidity(prev =>  ({...prev, [name]: value }));
    };

    const resetForm = () => {
        setFormData(data ?? {});
        setFormDataDebounced(data ?? {});
        setFormHiddenData({});
        setValidity({});
        setIsValid(false);
    };

    useEffect(() => {
        onChange && onChange(formData);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formData]);

    useEffect(() => {
        setIsValid(Object.values(formValidity).every(v => v === true));
    }, [formValidity]);

    useEffect(() => {
        if (debounce) {
            clearTimeout(debounce);
        }
        debounce = setTimeout(() => {
            setFormDataDebounced(formData);
        }, debounceTime);
    }, [formData]);

    const handleFieldVisibility = (name, isVisible) => {
        if (isVisible === null || isVisible === undefined) {
            // Field probably doesn't have a conditional, do nothing
            return;
        }
        if (isVisible === true) {
            if (formHiddenData[name] !== undefined) {
                setFormData(prev => ({[name]: formHiddenData[name], ...prev}));
                setFormHiddenData(prev => {
                    const newHiddenData = {...prev};
                    delete newHiddenData[name];
                    return newHiddenData;
                });
            }
        }

        if (isVisible === false) {
            setFormHiddenData(prev => ({...prev, [name]: formData[name]}));
            setFormData(prev => {
                const newData = {...prev};
                delete newData[name];
                // If there is data provided to the form, we need to add it back in
                if (restoreDataOnConditionalChange && data && data[name]) {
                    newData[name] = data[name];
                }
                return newData;
            });
            setValidity(prev => {
                const newValidData = {...prev};
                delete newValidData[name];
                return newValidData;
            });
        }
    }

    const providerValues = {
        formData,
        formDataDebounced,
        setFormData,
        updateField,
        updateValidity,
        isValid,
        isValidating,
        invalidFields: Object.keys(formValidity).filter(key => formValidity[key] === false && !formHiddenData[key]),
        setIsValidating,
        isSubmitting,
        setIsSubmitting,
        formDisabled: !!disabled || (!availableOffline && !isOnline),
        closeModalOnSubmit,
        goBackOnSubmit,
        afterSubmit,
        afterError,
        handleFieldVisibility,
        conditionalDependencies,
        layout,
        resetOnSubmit,
        resetForm,
    };

    return (
        <FormContext.Provider value={providerValues}>
            {children}
        </FormContext.Provider>
    );
};
